import React, { useState, useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import BackHeader from '../components/BackHeader';
import NavigationBar from '../components/NavigationBar';
import useTranslation from '../hooks/useTranslation';
import { faqSections } from '../data/faq';
import { getCurrentLanguage } from '../config/languageConfig';
import { showChannelTalk, trackChannelTalkEvent } from '../services/ChannelService';

const FAQPage: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());

  // 언어 변경 감지
  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLang(getCurrentLanguage());
    };

    window.addEventListener('languageChange', handleLanguageChange);
    return () => {
      window.removeEventListener('languageChange', handleLanguageChange);
    };
  }, []);

  // 검색 필터링
  const filteredSections = useMemo(() => {
    if (!searchQuery.trim()) {
      return faqSections;
    }
    
    return faqSections.filter(section => {
      const sectionTitle = t(section.title).toLowerCase();
      const searchLower = searchQuery.toLowerCase();
      
      // 섹션 제목에서 검색
      if (sectionTitle.includes(searchLower)) {
        return true;
      }
      
      // 질문과 답변에서 검색
      return section.items.some(item => {
        const question = item.question[currentLang].toLowerCase();
        const answer = item.answer[currentLang].toLowerCase();
        return question.includes(searchLower) || answer.includes(searchLower);
      });
    });
  }, [searchQuery, currentLang, t]);

  // 섹션 클릭 - 해당 섹션 페이지로 이동
  const handleSectionClick = (sectionId: string) => {
    navigate(`/faq/${sectionId}`);
  };

  // 채널톡 열기
  const handleGetHelp = async () => {
    try {
      await showChannelTalk();
      trackChannelTalkEvent('faq_get_help_clicked', {
        page: location.pathname,
        source: 'faq_page',
        search_query: searchQuery
      });
    } catch (error) {
      console.error('Failed to open ChannelTalk from FAQ:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 헤더 */}
      <BackHeader title={t('faq.title')} />
      
      {/* FAQ 배너 이미지 영역 */}
      <div className="relative bg-gradient-to-br from-orange-400 via-pink-400 to-blue-400 h-24 flex flex-col items-center justify-center px-4">
        {/* 검색 바 */}
        <div className="w-full max-w-md">
          <div className="bg-white rounded-xl shadow-lg p-1">
            <div className="flex items-center">
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-5 w-5 text-gray-400 ml-3" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" 
                />
              </svg>
              <input
                type="text"
                placeholder={t('faq.search.placeholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 px-3 py-2 text-gray-700 placeholder-gray-400 bg-transparent border-none outline-none"
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* FAQ 섹션 리스트 */}
      <div className="px-4 py-4 space-y-2">
        {filteredSections.map((section) => (
          <div
            key={section.id}
            onClick={() => handleSectionClick(section.id)}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 cursor-pointer hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">
                {t(section.title)}
              </h3>
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-5 w-5 text-gray-400" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M9 5l7 7-7 7" 
                />
              </svg>
            </div>
          </div>
        ))}
      </div>

      {/* Get Help 섹션 */}
      <div className="px-4 ">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            {t('faq.cantFindAnswer')}
          </h3>
          <p className="text-gray-600 mb-4">
            {t('faq.getHelpDescription')}
          </p>
          <button
            onClick={handleGetHelp}
            className="bg-red-500 hover:bg-red-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
          >
            {t('faq.getHelpNow')}
          </button>
        </div>
      </div>

      {/* 하단 여백 (NavigationBar 공간 확보) */}
      <div className="h-40"></div>

      {/* Navigation Bar */}
      <NavigationBar />
    </div>
  );
};

export default FAQPage; 